'use client';

import React, { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { usePasskey } from '~/hooks/usePasskey';
import { useRouter } from 'next/navigation';
import { useSession } from '~/hooks/useSession';
import { useSocket } from '~/hooks/useSocket';
import { EVENTS } from '~/lib/socket';

interface DeviceApprovalContextType {
  navigateToDevices: () => void;
  pendingRequestsCount: number;
  isCheckingApproval: boolean;
  approvalStatus: 'approved' | 'pending' | 'rejected';
}

const DeviceApprovalContext = createContext<DeviceApprovalContextType | undefined>(undefined);

interface DeviceApprovalProviderProps {
  children: ReactNode;
}

export function DeviceApprovalProvider({ children }: DeviceApprovalProviderProps) {
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const { session, status } = useSession();
  const { getSessionPasskey, getPendingApprovalRequests } = usePasskey();
  const { addEventListener } = useSocket();
  const [isCheckingApproval, setIsCheckingApproval] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<any>(null);
  const [myPasskey, setMyPasskey] = useState<any>(null);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const router = useRouter();

  // Only run device approval logic when user is in the right state
  const shouldCheckApproval =
    status === 'authenticated' &&
    session?.user?.onboardingCompleted &&
    !session?.hasApprovedPasskey;

  // Load passkey data
  useEffect(() => {
    if (shouldCheckApproval) {
      getSessionPasskey().then(setMyPasskey).catch(console.error);
      getPendingApprovalRequests().then((data) => setPendingApprovals(data?.requests || [])).catch(console.error);
    }
  }, [shouldCheckApproval, getPendingApprovalRequests, getSessionPasskey]);

  // Socket.IO real-time device approval updates
  useEffect(() => {
    if (!session?.user?.id) return;

    const handleDeviceApprovalRequest = (data: any) => {
      console.log('New device approval request:', data);
      setPendingRequestsCount((prev) => prev + 1);
      // Optionally navigate to devices page for new requests
      if (session?.hasApprovedPasskey) {
        router.push('/devices');
      }
    };

    const handleDeviceApproved = (data: any) => {
      console.log('Device approved:', data);
      setApprovalStatus('approved');
      setIsCheckingApproval(false);
      // Refresh passkey status or navigate as needed
    };

    const handleDeviceRejected = (data: any) => {
      console.log('Device rejected:', data);
      setApprovalStatus('rejected');
      setIsCheckingApproval(false);
    };

    const removeRequestListener = addEventListener(
      EVENTS.DEVICE_APPROVAL_REQUEST,
      handleDeviceApprovalRequest
    );
    const removeApprovedListener = addEventListener(EVENTS.DEVICE_APPROVED, handleDeviceApproved);
    const removeRejectedListener = addEventListener(EVENTS.DEVICE_REJECTED, handleDeviceRejected);

    return () => {
      removeRequestListener();
      removeApprovedListener();
      removeRejectedListener();
    };
  }, [session?.user?.id, session?.hasApprovedPasskey, addEventListener, router]);

  const checkDeviceApprovalStatus = useCallback(async () => {
    try {
      setIsCheckingApproval(true);

      // If passkey is already approved, stop checking
      if (myPasskey?.approved) {
        setApprovalStatus('approved');
        setIsCheckingApproval(false);
        return;
      }

      // If no passkey found, but we have a passkeyId in session,
      // it might be a race condition - keep checking
      if (!myPasskey && session?.passkeyId) {
        // Keep the current status if we're already checking, otherwise set to pending
        if (approvalStatus === null) {
          setApprovalStatus('pending');
        }
        // Remove setTimeout - Socket.IO will handle real-time updates
        return;
      }

      // If no passkey and no passkeyId, truly rejected
      if (!myPasskey && !session?.passkeyId) {
        setApprovalStatus('rejected');
        setIsCheckingApproval(false);
        return;
      }

      // Passkey exists but not approved - pending
      if (myPasskey && !myPasskey.approved) {
        setApprovalStatus('pending');
        // Remove setTimeout - Socket.IO will handle real-time updates
      }
    } catch (error) {
      console.error('Failed to check device approval status:', error);
      // Only set to rejected if we don't have a passkeyId (indicating a real failure)
      if (!session?.passkeyId) {
        setApprovalStatus('rejected');
      }
      setIsCheckingApproval(false);
    }
  }, [myPasskey, session?.passkeyId, approvalStatus]);

  const checkForPendingRequests = useCallback(async () => {
    try {
      const requests = pendingApprovals.filter((p: any) => p.status === 'pending');
      const newCount = requests?.length || 0;
      setPendingRequestsCount(newCount);
    } catch (error) {
      console.error('Failed to check for pending approval requests:', error);
    }
  }, [pendingApprovals]);

  useEffect(() => {
    if (shouldCheckApproval) {
      checkDeviceApprovalStatus();
    }
  }, [shouldCheckApproval, checkDeviceApprovalStatus]);

  // Initial check for pending requests (for approved devices)
  useEffect(() => {
    if (
      status === 'authenticated' &&
      session?.user?.onboardingCompleted &&
      session?.hasApprovedPasskey
    ) {
      checkForPendingRequests();
      // Removed polling - Socket.IO will handle real-time updates
    }
  }, [status, session?.user?.onboardingCompleted, session?.hasApprovedPasskey, checkForPendingRequests]);

  const navigateToDevices = () => {
    router.push('/devices');
  };

  const contextValue: DeviceApprovalContextType = {
    navigateToDevices,
    pendingRequestsCount,
    isCheckingApproval,
    approvalStatus,
  };

  return (
    <DeviceApprovalContext.Provider value={contextValue}>{children}</DeviceApprovalContext.Provider>
  );
}

export function useDeviceApproval(): DeviceApprovalContextType {
  const context = useContext(DeviceApprovalContext);
  if (!context) {
    throw new Error('useDeviceApproval must be used within a DeviceApprovalProvider');
  }
  return context;
}
